// src/components/ChatWidget.js

import React, { useState, useEffect } from "react";
import { useChat } from "../hooks/useChat";
import "./ChatWidget.css";
import ChatWithUsLogo from "../assets/chat_with_us.webp";

// 💡 Accept new props: isControlledOpen and onClose
const ChatWidget = ({ isControlledOpen, onClose }) => { 
  const {
    isConnected,
    messages,
    isChatActive,
    startNewConversation,
    sendMessage,
  } = useChat();
  
  // 💡 Internal state is used only if not controlled by parent
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [inputText, setInputText] = useState("");

  // Determine the actual 'isOpen' state: prefer controlled prop, fallback to internal state
  const isOpen = isControlledOpen !== undefined ? isControlledOpen : internalIsOpen;

  // 💡 Effect to start conversation when the component opens (only if using the controlled prop)
  useEffect(() => {
    if (isControlledOpen && !isChatActive) {
      startNewConversation();
    }
  }, [isControlledOpen, isChatActive, startNewConversation]);

  // 💡 This handler now manages both controlled and internal closing
  const handleClose = () => {
    if (onClose) {
      // If controlled, call the parent's onClose handler
      onClose();
    } else {
      // If not controlled, toggle the internal state
      setInternalIsOpen(false);
    }
  };

  const handleButtonClick = () => {
    if (!isChatActive) {
      startNewConversation();
    }
    // Only toggle internal state if not controlled
    if (isControlledOpen === undefined) {
      setInternalIsOpen(!internalIsOpen);
    }
  };

  const handleSend = (e) => {
    e.preventDefault();
    sendMessage(inputText);
    setInputText("");
  };

  // 💡 Check if the component is in a controlled state (rendered by SocialMediaBar)
  const isControlled = isControlledOpen !== undefined;

  // 💡 Use a consistent container class for the chat window, regardless of control method
  return (
    <div className={`chat-container ${isControlled ? 'as-modal' : 'as-floating-button'}`}>
      
      {/* The Chat Window Modal */}
      {isOpen && (
        <div className="chat-window">
          <div className="chat-header">
            Live Support
            {/* 💡 Use the unified handleClose function */}
            <span
              onClick={handleClose}
              style={{ color: "red", cursor: "pointer" }}
            >
              X
            </span>
          </div>

          <div className="chat-messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`message ${
                  msg.senderType === "Customer" ? "sent" : "received"
                }`}
              >
                <strong>{msg.senderType === "Customer" ? "You" : "Fitoja"}:</strong> {msg.text}
              </div>
            ))}
            {!isChatActive && (
              <p>Click "Start Chat" to begin a conversation.</p>
            )}
          </div>

          <form onSubmit={handleSend} className="chat-input-form">
            <input
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder="Type a message..."
              disabled={!isConnected || !isChatActive}
            />
            <button
              type="submit"
              disabled={!isConnected || !isChatActive || !inputText.trim()}
            >
              Send
            </button>
          </form>
        </div>
      )}

      {/* 💡 The Floating Bubble Button is only rendered if NOT controlled by a parent */}
      {!isControlled && (
        <img
          style={{
            display: isOpen ? "none" : "flex",
            height: "60px",
            cursor: "pointer",
          }}
          onClick={handleButtonClick}
          src={ChatWithUsLogo}
          alt="Chat with us"
        />
      )}
    </div>
  );
};

export default ChatWidget;